import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CausesByParentIssueIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetCausesByParentIssueIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC cause data to match the GraphQL query structure
 */
function mapTrpcCausesToGraphQL(
  trpcData: CausesByParentIssueIdResponseRow[] | undefined
): GetCausesByParentIssueIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    cause: trpcData,
  };
}

export const useGetCausesByParentIssueId = (
  parentIssueId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.cause.getByParentIssueId.queryOptions({
      issueId: parentIssueId,
    }),

    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcCausesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
  };
};
